import React, { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Box, Container, Slider, Typography } from "@mui/material";
import BoltIcon from "@mui/icons-material/Bolt";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import BatteryChargingFullIcon from "@mui/icons-material/BatteryChargingFull";
import AssignmentIcon from "@mui/icons-material/Assignment";
import MapIcon from "@mui/icons-material/Map";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StaticPageLayout from "../../components/StaticPageLayout";
import { QuoteModal } from "../../components/QuoteModal";
import { ZipQuoteForm } from "../Home";
import * as styles from "./styles";

type TranslateFnT = (key: string, options?: Record<string, unknown>) => string;

type InViewResultT = {
  ref: React.RefObject<HTMLDivElement | null>;
  inView: boolean;
};

const useInView = (threshold = 0.15): InViewResultT => {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setInView(true);
      },
      { threshold },
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, inView };
};

type BenefitCardT = {
  icon: React.ReactNode;
  title: string;
  body: string;
};

type StatItemT = {
  number: string;
  label: string;
};

type ProcessStepT = {
  number: string;
  title: string;
  description: string;
};

const CARD_GLOWS = [
  {
    top: 0,
    left: 0,
    right: "auto",
    bottom: "auto",
    background: "radial-gradient(ellipse at top left, rgba(56,189,248,0.18) 0%, transparent 70%)",
  },
  {
    top: 0,
    left: "auto",
    right: 0,
    bottom: "auto",
    background: "radial-gradient(ellipse at top right, rgba(103,232,249,0.16) 0%, transparent 70%)",
  },
  {
    top: "auto",
    left: 0,
    right: "auto",
    bottom: 0,
    background: "radial-gradient(ellipse at bottom left, rgba(59,130,246,0.18) 0%, transparent 70%)",
  },
  {
    top: 0,
    left: 0,
    right: "auto",
    bottom: "auto",
    background: "radial-gradient(ellipse at top left, rgba(103,232,249,0.15) 0%, transparent 65%)",
  },
  {
    top: "auto",
    left: "auto",
    right: 0,
    bottom: 0,
    background: "radial-gradient(ellipse at bottom right, rgba(56,189,248,0.17) 0%, transparent 70%)",
  },
  {
    top: 0,
    left: 0,
    right: "auto",
    bottom: "auto",
    background:
      "radial-gradient(ellipse at top left, rgba(56,189,248,0.14) 0%, rgba(103,232,249,0.06) 50%, transparent 70%)",
  },
];

type HeroSectionPropsT = { onQuoteOpen: (zip: string) => void; t: TranslateFnT; statItems: StatItemT[] };

const HeroSection = ({ onQuoteOpen, t, statItems }: HeroSectionPropsT) => {
  const handleScrollDown = () => {
    const el = document.getElementById("stats-bar");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (!document.querySelector("script[src='https://player.vimeo.com/api/player.js']")) {
      const script = document.createElement("script");
      script.src = "https://player.vimeo.com/api/player.js";
      document.body.appendChild(script);
    }
  }, []);

  return (
    <Box sx={styles.heroOuter}>
      <Box
        component="iframe"
        src="https://player.vimeo.com/video/512638353?h=9603c255d6&background=1&autoplay=1&loop=1&muted=1&byline=0&title=0"
        allow="autoplay; fullscreen"
        sx={styles.heroIframe}
      />
      <Box sx={styles.heroRadialOverlay} />
      <Box sx={styles.heroBottomGradient} />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2 }}>
        <Box sx={{ textAlign: "center", mx: "auto" }}>
          <Typography sx={styles.heroEyebrow}>{t("hero.eyebrow")}</Typography>

          <Typography variant="h1" sx={styles.heroTitleLine}>
            {t("hero.headlineWhite")}
            <Box component="span" sx={styles.gradientText}>
              {t("hero.headlineGradient")}
            </Box>
          </Typography>
          <Typography variant="h1" sx={styles.heroTitleLineTwo}>
            {t("hero.headlineSecondLine")}
          </Typography>

          <Typography sx={styles.heroSubtitle}>{t("hero.subtitle")}</Typography>

          <Box sx={styles.heroStatsRow}>
            {statItems.map((stat) => (
              <Box key={stat.label} sx={{ textAlign: "center" }}>
                <Typography sx={styles.statNumber}>{stat.number}</Typography>
                <Typography sx={styles.statLabel}>{stat.label}</Typography>
              </Box>
            ))}
          </Box>

          <Box sx={styles.heroCtaRow}>
            <ZipQuoteForm ctaLabel={t("hero.ctaLabel")} onSubmit={onQuoteOpen} />
          </Box>
        </Box>
      </Container>

      <Box onClick={handleScrollDown} sx={styles.scrollIndicator}>
        <Typography sx={styles.scrollLabel}>{t("hero.scrollLabel")}</Typography>
        <KeyboardArrowDownIcon sx={{ fontSize: "1.4rem", color: "rgba(255,255,255,0.5)" }} />
      </Box>
    </Box>
  );
};

const BenefitCard = ({ card, delay, index }: { card: BenefitCardT; delay: string; index: number }) => {
  const { ref, inView } = useInView();
  const glow = CARD_GLOWS[index % CARD_GLOWS.length];
  return (
    <Box
      ref={ref}
      sx={{
        ...styles.glassmorphismCard,
        p: 4,
        height: "100%",
        position: "relative",
        overflow: "hidden",
        opacity: inView ? 1 : 0,
        transform: inView ? "translateY(0) scale(1)" : "translateY(40px) scale(0.92)",
        transition: `opacity 0.65s cubic-bezier(0.22,1,0.36,1) ${delay}, transform 0.65s cubic-bezier(0.22,1,0.36,1) ${delay}`,
        "&:hover": {
          transform: "translateY(-4px)",
          border: "1px solid rgba(56,189,248,0.32)",
          boxShadow: "0 20px 60px rgba(0,0,0,0.4), 0 0 40px rgba(56,189,248,0.12)",
        },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: glow.top,
          left: glow.left,
          right: glow.right,
          bottom: glow.bottom,
          width: "70%",
          height: "70%",
          background: glow.background,
          pointerEvents: "none",
        }}
      />
      <Box sx={{ mb: 2, position: "relative" }}>{card.icon}</Box>
      <Typography variant="h6" sx={styles.benefitCardTitle}>
        {card.title}
      </Typography>
      <Typography sx={styles.benefitCardBody}>{card.body}</Typography>
    </Box>
  );
};

type BenefitsSectionPropsT = { t: TranslateFnT; benefitCards: BenefitCardT[] };

const BenefitsSection = ({ t, benefitCards }: BenefitsSectionPropsT) => {
  const { ref, inView } = useInView();
  return (
    <Box sx={styles.benefitsOuter}>
      <Container maxWidth="lg">
        <Box
          ref={ref}
          sx={{
            textAlign: "center",
            mb: 8,
            opacity: inView ? 1 : 0,
            transform: inView ? "translateY(0)" : "translateY(32px)",
            transition: "opacity 0.7s ease, transform 0.7s ease",
          }}
        >
          <Typography sx={styles.eyebrowSx}>{t("benefits.eyebrow")}</Typography>
          <Typography variant="h2" sx={styles.benefitsSectionTitle}>
            {t("benefits.headlineBase")}
            <Box component="span" sx={styles.gradientText}>
              {t("benefits.headlineGradient")}
            </Box>
          </Typography>
          <Typography sx={styles.benefitsSectionSubtitle}>{t("benefits.subtitle")}</Typography>
        </Box>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
          {benefitCards.map((card, i) => (
            <Box key={card.title} sx={{ width: { xs: "100%", sm: "calc(50% - 12px)", md: "calc(33.333% - 16px)" } }}>
              <BenefitCard card={card} delay={`${i * 0.12}s`} index={i} />
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

const ProcessStepItem = ({ step, index }: { step: ProcessStepT; index: number }) => {
  const { ref, inView } = useInView();
  return (
    <Box
      ref={ref}
      sx={{
        flex: 1,
        textAlign: "center",
        px: { xs: 2, md: 4 },
        position: "relative",
        zIndex: 1,
        opacity: inView ? 1 : 0,
        transform: inView ? "translateY(0)" : "translateY(32px)",
        transition: `opacity 0.7s ease ${index * 0.15}s, transform 0.7s ease ${index * 0.15}s`,
      }}
    >
      <Typography sx={styles.processStepNumber}>{step.number}</Typography>
      <Typography variant="h6" sx={styles.processStepTitle}>
        {step.title}
      </Typography>
      <Typography sx={styles.processStepDescription}>{step.description}</Typography>
    </Box>
  );
};

type HowSolarWorksSectionPropsT = { t: TranslateFnT; processSteps: ProcessStepT[] };

const HowSolarWorksSection = ({ t, processSteps }: HowSolarWorksSectionPropsT) => {
  const { ref, inView } = useInView();
  return (
    <Box sx={styles.howSolarWorksOuter}>
      <Box sx={styles.howSolarWorksOverlay} />
      <Box sx={styles.howSolarWorksRadialOverlay} />
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Box
          ref={ref}
          sx={{
            textAlign: "center",
            mb: 8,
            opacity: inView ? 1 : 0,
            transform: inView ? "translateY(0)" : "translateY(32px)",
            transition: "opacity 0.7s ease, transform 0.7s ease",
          }}
        >
          <Typography sx={styles.eyebrowSx}>{t("process.eyebrow")}</Typography>
          <Typography variant="h2" sx={styles.sectionTitle}>
            {t("process.headlineBase")}
            <Box component="span" sx={styles.gradientText}>
              {t("process.headlineGradient")}
            </Box>
          </Typography>
        </Box>

        <Box sx={styles.processStepsRow}>
          <Box sx={styles.processStepsLine} />
          {processSteps.map((step, i) => (
            <ProcessStepItem key={step.number} step={step} index={i} />
          ))}
        </Box>
      </Container>
    </Box>
  );
};

type SavingsSectionPropsT = { t: TranslateFnT; savingsBullets: string[] };

const SavingsSection = ({ t, savingsBullets }: SavingsSectionPropsT) => {
  const { ref: headingRef, inView: headingInView } = useInView();
  const { ref: cardRef, inView: cardInView } = useInView();
  const [bill, setBill] = useState(150);

  const solarMonthly = Math.round(bill * 0.78);
  const savings25yr = Math.round(bill * 265.8);
  const savings25yrLabel = savings25yr >= 1000 ? `$${Math.round(savings25yr / 1000)}K` : `$${savings25yr}`;

  return (
    <Box sx={styles.savingsOuter}>
      <Container maxWidth="lg">
        <Box sx={{ display: { xs: "block", md: "flex" }, gap: { md: "64px" }, alignItems: "center" }}>
          <Box sx={{ flex: 1, minWidth: 0, mb: { xs: 6, md: 0 } }}>
            <Box
              ref={headingRef}
              sx={{
                opacity: headingInView ? 1 : 0,
                transform: headingInView ? "translateY(0)" : "translateY(32px)",
                transition: "opacity 0.7s ease, transform 0.7s ease",
              }}
            >
              <Typography sx={styles.eyebrowSx}>{t("savings.eyebrow")}</Typography>
              <Typography variant="h2" sx={styles.savingsSectionTitle}>
                {t("savings.headlineBase")}
                <Box component="span" sx={styles.gradientText}>
                  {t("savings.headlineGradient")}
                </Box>
              </Typography>
              <Typography sx={styles.savingsDescription}>{t("savings.description")}</Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {savingsBullets.map((bullet) => (
                  <Box key={bullet} sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                    <CheckCircleOutlineIcon sx={{ color: "#38bdf8", fontSize: "1.25rem", mt: "2px", flexShrink: 0 }} />
                    <Typography sx={styles.bulletText}>{bullet}</Typography>
                  </Box>
                ))}
              </Box>

              <Box sx={styles.sliderLabelRow}>
                <Typography sx={styles.sliderLabelTitle}>{t("savings.sliderLabel")}</Typography>
                <Typography sx={styles.sliderLabelValue}>${bill}</Typography>
              </Box>
              <Slider
                value={bill}
                onChange={(_, val) => setBill(val as number)}
                min={60}
                max={500}
                step={10}
                sx={styles.slider}
              />
              <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
                <Typography sx={styles.sliderRangeLabel}>$60</Typography>
                <Typography sx={styles.sliderRangeLabel}>$500</Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              ref={cardRef}
              sx={{
                ...styles.glassmorphismCard,
                overflow: "hidden",
                p: 0,
                opacity: cardInView ? 1 : 0,
                transform: cardInView ? "translateY(0) scale(1)" : "translateY(40px) scale(0.95)",
                transition: "opacity 0.7s ease 0.2s, transform 0.7s ease 0.2s",
              }}
            >
              <Box sx={styles.comparisonCardGradientBar} />
              <Box sx={styles.comparisonCardHeader}>
                <Typography sx={styles.comparisonCardHeaderLabel}>{t("savings.comparisonCard.heading")}</Typography>
              </Box>

              <Box sx={styles.utilityRow}>
                <Box>
                  <Typography sx={styles.rowTitle}>{t("savings.comparisonCard.utilityTitle")}</Typography>
                  <Typography sx={styles.rowSubtitle}>{t("savings.comparisonCard.utilitySubtitle")}</Typography>
                </Box>
                <Box sx={{ textAlign: "right" }}>
                  <Typography component="span" sx={styles.utilityPrice}>
                    ${bill}
                  </Typography>
                  <Typography component="span" sx={styles.priceUnit}>
                    {t("savings.comparisonCard.priceUnit")}
                  </Typography>
                </Box>
              </Box>

              <Box sx={styles.solarRow}>
                <Box>
                  <Typography sx={styles.rowTitle}>{t("savings.comparisonCard.solarTitle")}</Typography>
                  <Typography sx={styles.rowSubtitle}>{t("savings.comparisonCard.solarSubtitle")}</Typography>
                </Box>
                <Box sx={{ textAlign: "right" }}>
                  <Typography component="span" sx={styles.solarPrice}>
                    ${solarMonthly}
                  </Typography>
                  <Typography component="span" sx={styles.priceUnit}>
                    {t("savings.comparisonCard.priceUnit")}
                  </Typography>
                </Box>
              </Box>

              <Box sx={styles.savingsTotalRow}>
                <Box>
                  <Typography sx={styles.rowTitle}>{t("savings.comparisonCard.savingsTitle")}</Typography>
                  <Typography sx={styles.rowSubtitle}>{t("savings.comparisonCard.savingsSubtitle")}</Typography>
                </Box>
                <Typography sx={styles.savingsTotalPrice}>+{savings25yrLabel}</Typography>
              </Box>

              <Box sx={{ px: 4, py: 2, borderTop: "1px solid rgba(56,189,248,0.08)" }}>
                <Typography sx={styles.comparisonDisclaimer}>{t("savings.comparisonCard.disclaimer")}</Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

type TestimonialItemT = {
  quote: string;
  name: string;
  location: string;
  initials: string;
};

type VideoFeatureSectionPropsT = { t: TranslateFnT; testimonials: TestimonialItemT[] };

const VideoFeatureSection = ({ t, testimonials }: VideoFeatureSectionPropsT) => {
  const { ref, inView } = useInView();
  const [videoPlaying, setVideoPlaying] = useState(false);

  return (
    <Box sx={{ ...styles.videoFeatureOuter, position: "relative", zIndex: videoPlaying ? 1201 : undefined }}>
      <Container maxWidth="lg">
        <Box
          ref={ref}
          sx={{
            textAlign: "center",
            mb: 6,
            opacity: inView ? 1 : 0,
            transform: inView ? "translateY(0)" : "translateY(32px)",
            transition: "opacity 0.7s ease, transform 0.7s ease",
          }}
        >
          <Typography sx={styles.eyebrowSx}>{t("videoFeature.eyebrow")}</Typography>
          <Typography variant="h2" sx={styles.sectionTitle}>
            {t("videoFeature.headlineStart")}
            <Box component="span" sx={styles.gradientText}>
              {t("videoFeature.headlineGradient")}
            </Box>
            {t("videoFeature.headlineEnd")}
          </Typography>
        </Box>

        <Box
          sx={{
            width: "100%",
            position: "relative",
            paddingTop: "56.25%",
            borderRadius: "12px",
            overflow: "hidden",
            opacity: inView ? 1 : 0,
            transform: inView ? "translateY(0) scale(1)" : "translateY(40px) scale(0.97)",
            transition: "opacity 0.7s ease 0.15s, transform 0.7s ease 0.15s",
          }}
        >
          {videoPlaying ? (
            <Box
              component="iframe"
              src="https://player.vimeo.com/video/862768968?badge=0&autopause=0&player_id=0&app_id=58479&autoplay=1"
              allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
              sx={styles.videoIframe}
            />
          ) : (
            <Box sx={styles.videoThumbnail}>
              <Box onClick={() => setVideoPlaying(true)} sx={styles.videoPlayOverlay}>
                <Box className="play-btn" sx={styles.playButton}>
                  <PlayArrowIcon sx={{ fontSize: { xs: 36, md: 46 }, color: "white" }} />
                </Box>
              </Box>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
            gap: 3,
            mt: 8,
            opacity: inView ? 1 : 0,
            transform: inView ? "translateY(0)" : "translateY(32px)",
            transition: "opacity 0.7s ease 0.3s, transform 0.7s ease 0.3s",
          }}
        >
          {testimonials.map((testimonial) => (
            <Box key={testimonial.name} sx={styles.testimonialCard}>
              <Box sx={{ display: "flex", gap: 0.5 }}>
                {["star-1", "star-2", "star-3", "star-4", "star-5"].map((key) => (
                  <Box key={key} component="span" sx={styles.starIcon}>
                    ★
                  </Box>
                ))}
              </Box>
              <Typography sx={styles.testimonialQuote}>&ldquo;{testimonial.quote}&rdquo;</Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box sx={styles.testimonialAvatar}>
                  <Typography sx={styles.testimonialInitials}>{testimonial.initials}</Typography>
                </Box>
                <Box>
                  <Typography sx={styles.testimonialName}>{testimonial.name}</Typography>
                  <Typography sx={styles.testimonialLocation}>{testimonial.location}</Typography>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

type AppSectionPropsT = { t: TranslateFnT; appFeatureBullets: string[] };

const AppSection = ({ t, appFeatureBullets }: AppSectionPropsT) => {
  const { ref: leftRef, inView: leftInView } = useInView();
  const { ref: rightRef, inView: rightInView } = useInView();

  return (
    <Box sx={styles.appSectionOuter}>
      <Box sx={styles.appSectionGlow} />
      <Box
        ref={leftRef}
        sx={{
          flex: "0 0 auto",
          width: { xs: "100%", md: "48%" },
          alignSelf: "stretch",
          opacity: leftInView ? 1 : 0,
          transition: "opacity 0.7s ease",
          position: "relative",
          zIndex: 1,
        }}
      >
        <Box
          component="video"
          autoPlay
          muted
          loop
          playsInline
          src="/img/assets/App-mockup3.mp4"
          sx={styles.appSectionVideo}
        />
      </Box>

      <Box sx={styles.appSectionRight}>
        <Box ref={rightRef}>
          <Typography
            sx={{
              ...styles.eyebrowSx,
              opacity: rightInView ? 1 : 0,
              transform: rightInView ? "translateY(0)" : "translateY(32px)",
              transition: "opacity 0.7s ease 0s, transform 0.7s ease 0s",
            }}
          >
            {t("app.eyebrow")}
          </Typography>

          <Typography
            variant="h2"
            sx={{
              ...styles.appSectionH2Line1,
              opacity: rightInView ? 1 : 0,
              transform: rightInView ? "translateY(0)" : "translateY(32px)",
              transition: "opacity 0.7s ease 0.1s, transform 0.7s ease 0.1s",
            }}
          >
            {t("app.headlineLine1")}
          </Typography>
          <Typography
            variant="h2"
            sx={{
              ...styles.appSectionH2Line2,
              opacity: rightInView ? 1 : 0,
              transform: rightInView ? "translateY(0)" : "translateY(32px)",
              transition: "opacity 0.7s ease 0.2s, transform 0.7s ease 0.2s",
            }}
          >
            {t("app.headlineLine2")}
          </Typography>

          <Typography
            sx={{
              ...styles.appSectionBody,
              opacity: rightInView ? 1 : 0,
              transform: rightInView ? "translateY(0)" : "translateY(32px)",
              transition: "opacity 0.7s ease 0.3s, transform 0.7s ease 0.3s",
            }}
          >
            {t("app.body")}
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              mb: 4,
              opacity: rightInView ? 1 : 0,
              transform: rightInView ? "translateY(0)" : "translateY(32px)",
              transition: "opacity 0.7s ease 0.4s, transform 0.7s ease 0.4s",
            }}
          >
            {appFeatureBullets.map((bullet) => (
              <Box key={bullet} sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                <CheckCircleOutlineIcon sx={{ color: "#38bdf8", fontSize: "1.25rem", mt: "2px", flexShrink: 0 }} />
                <Typography sx={styles.bulletText}>{bullet}</Typography>
              </Box>
            ))}
          </Box>

          <Box
            sx={{
              ...styles.appStoreBadge,
              opacity: rightInView ? 1 : 0,
              transform: rightInView ? "translateY(0)" : "translateY(32px)",
              transition: "opacity 0.7s ease 0.5s, transform 0.7s ease 0.5s",
            }}
          >
            <Typography sx={styles.appStoreBadgeText}>{t("app.storeBadge")}</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

type CtaSectionPropsT = { onQuoteOpen: (zip: string) => void; t: TranslateFnT };

const CtaSection = ({ onQuoteOpen, t }: CtaSectionPropsT) => {
  const { ref, inView } = useInView();
  return (
    <Box sx={styles.ctaOuter}>
      <Box sx={styles.ctaOverlay} />
      <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
        <Box
          ref={ref}
          sx={{
            opacity: inView ? 1 : 0,
            transform: inView ? "translateY(0)" : "translateY(32px)",
            transition: "opacity 0.7s ease, transform 0.7s ease",
          }}
        >
          <Typography sx={styles.eyebrowSx}>{t("cta.eyebrow")}</Typography>
          <Typography variant="h2" sx={styles.ctaSectionTitle}>
            {t("cta.headlineBase")}
            <Box component="span" sx={styles.gradientText}>
              {t("cta.headlineGradient")}
            </Box>
          </Typography>
          <Typography sx={styles.ctaSubtitle}>{t("cta.subtitle")}</Typography>
          <Box sx={styles.ctaButtonRow}>
            <ZipQuoteForm ctaLabel={t("cta.ctaLabel")} onSubmit={onQuoteOpen} />
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

const WhySolarContent = () => {
  const { t } = useTranslation("static_why_solar");
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteZip, setQuoteZip] = useState("");

  const handleQuoteOpen = (zip: string) => {
    setQuoteZip(zip);
    setQuoteOpen(true);
  };

  const benefitCards: BenefitCardT[] = [
    {
      icon: <BoltIcon sx={{ fontSize: 36, color: "#38bdf8" }} />,
      title: t("benefits.cards.risingBills.title"),
      body: t("benefits.cards.risingBills.body"),
    },
    {
      icon: <TrendingUpIcon sx={{ fontSize: 36, color: "#38bdf8" }} />,
      title: t("benefits.cards.homeValue.title"),
      body: t("benefits.cards.homeValue.body"),
    },
    {
      icon: <AttachMoneyIcon sx={{ fontSize: 36, color: "#38bdf8" }} />,
      title: t("benefits.cards.realSavings.title"),
      body: t("benefits.cards.realSavings.body"),
    },
    {
      icon: <BatteryChargingFullIcon sx={{ fontSize: 36, color: "#38bdf8" }} />,
      title: t("benefits.cards.energyIndependence.title"),
      body: t("benefits.cards.energyIndependence.body"),
    },
    {
      icon: <AssignmentIcon sx={{ fontSize: 36, color: "#38bdf8" }} />,
      title: t("benefits.cards.zerDown.title"),
      body: t("benefits.cards.zerDown.body"),
    },
    {
      icon: <MapIcon sx={{ fontSize: 36, color: "#38bdf8" }} />,
      title: t("benefits.cards.stateIncentives.title"),
      body: t("benefits.cards.stateIncentives.body"),
    },
  ];

  const statItems: StatItemT[] = [
    { number: t("stats.energySavings.number"), label: t("stats.energySavings.label") },
    { number: t("stats.homeValue.number"), label: t("stats.homeValue.label") },
    { number: t("stats.lifespan.number"), label: t("stats.lifespan.label") },
  ];

  const processSteps: ProcessStepT[] = [
    {
      number: t("process.steps.step01.number"),
      title: t("process.steps.step01.title"),
      description: t("process.steps.step01.description"),
    },
    {
      number: t("process.steps.step02.number"),
      title: t("process.steps.step02.title"),
      description: t("process.steps.step02.description"),
    },
    {
      number: t("process.steps.step03.number"),
      title: t("process.steps.step03.title"),
      description: t("process.steps.step03.description"),
    },
    {
      number: t("process.steps.step04.number"),
      title: t("process.steps.step04.title"),
      description: t("process.steps.step04.description"),
    },
  ];

  const savingsBullets: string[] = [
    t("savings.bullets.eliminateBill"),
    t("savings.bullets.protectRates"),
    t("savings.bullets.increaseValue"),
  ];

  const appFeatureBullets: string[] = [
    t("app.bullets.aiInsights"),
    t("app.bullets.liveProgress"),
    t("app.bullets.dashboard"),
  ];

  const testimonials: TestimonialItemT[] = [
    {
      quote: t("videoFeature.testimonials.maria.quote"),
      name: t("videoFeature.testimonials.maria.name"),
      location: t("videoFeature.testimonials.maria.location"),
      initials: t("videoFeature.testimonials.maria.initials"),
    },
    {
      quote: t("videoFeature.testimonials.james.quote"),
      name: t("videoFeature.testimonials.james.name"),
      location: t("videoFeature.testimonials.james.location"),
      initials: t("videoFeature.testimonials.james.initials"),
    },
    {
      quote: t("videoFeature.testimonials.sandra.quote"),
      name: t("videoFeature.testimonials.sandra.name"),
      location: t("videoFeature.testimonials.sandra.location"),
      initials: t("videoFeature.testimonials.sandra.initials"),
    },
  ];

  return (
    <>
      <HeroSection onQuoteOpen={handleQuoteOpen} t={t} statItems={statItems} />
      <BenefitsSection t={t} benefitCards={benefitCards} />
      <HowSolarWorksSection t={t} processSteps={processSteps} />
      <SavingsSection t={t} savingsBullets={savingsBullets} />
      <AppSection t={t} appFeatureBullets={appFeatureBullets} />
      <VideoFeatureSection t={t} testimonials={testimonials} />
      <CtaSection onQuoteOpen={handleQuoteOpen} t={t} />
      <QuoteModal open={quoteOpen} zip={quoteZip} onClose={() => setQuoteOpen(false)} />
    </>
  );
};

const WhySolarPage = () => {
  const { t } = useTranslation("static_why_solar");
  return (
    <StaticPageLayout
      title={t("meta.title")}
      description={t("meta.description")}
      canonicalUrl="https://powur.com/whysolar"
      ogTitle={t("meta.ogTitle")}
      ogDescription={t("meta.ogDescription")}
      noTopSpacer
    >
      <WhySolarContent />
    </StaticPageLayout>
  );
};

export default WhySolarPage;
